import { useParams, Link, Navigate } from "react-router-dom";
import {
  Star,
  Quotes,
  ArrowRight,
  CheckCircle,
  MapPin,
} from "@phosphor-icons/react";
import SEO from "../components/SEO";
import CallButton from "../components/CallButton";
import TrustBar from "../components/TrustBar";
import StaticMap from "../components/StaticMap";
import FinalCTABanner from "../components/FinalCTABanner";
import ServiceCard from "../components/ServiceCard";
import { CITIES, getCity } from "../data/cities";
import { SERVICES } from "../data/services";
import { BUSINESS, IMAGES } from "../data/constants";

export default function CityDetail() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <Navigate to="/404" replace />;

  const otherCities = CITIES.filter((c) => c.slug !== slug).slice(0, 6);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        name: `${BUSINESS.shortName} - ${city.name}`,
        telephone: BUSINESS.phone,
        areaServed: { "@type": "City", name: city.name },
        address: {
          "@type": "PostalAddress",
          addressLocality: city.name,
          addressRegion: "ON",
          addressCountry: "CA",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BUSINESS.url },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${BUSINESS.url}/areas` },
          { "@type": "ListItem", position: 3, name: city.name, item: `${BUSINESS.url}/areas/${city.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={city.metaTitle}
        description={city.metaDescription}
        canonical={`/areas/${city.slug}`}
        ogImage={IMAGES.torontoSkyline}
        schema={schema}
      />

      <section className="relative bg-[#1B2A4A] text-white" data-testid="city-hero">
        <img
          src={IMAGES.torontoSkyline}
          alt={`${city.name} GTA skyline and service area`}
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A] via-[#1B2A4A]/90 to-[#1B2A4A]/50" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24">
          <nav className="text-xs text-white/60 font-bold uppercase tracking-widest font-display">
            <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/areas" className="hover:text-[#FF6B00]">Service Areas</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="mt-5 inline-flex items-center gap-2 rounded-sm bg-[#FF6B00] px-3 py-1.5 text-xs font-bold font-display uppercase tracking-widest">
            <MapPin size={14} weight="fill" /> Serving {city.name} 24/7
          </div>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl leading-[1.05]">
            Garage Door Repair & Locksmith Services in{" "}
            <span className="text-[#FF6B00]">{city.name}</span>
          </h1>
          <div className="mt-8">
            <CallButton size="lg" testId="city-hero-call-button" />
          </div>
        </div>
      </section>

      <TrustBar variant="dark" />

      <section className="section-pad bg-white">
        <div className="container-tight">
          <p className="text-base md:text-lg text-[#1B2A4A] leading-relaxed max-w-3xl" data-testid="city-intro">
            {city.intro}
          </p>

          <div className="mt-10 rounded-sm bg-[#1B2A4A] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="city-trust-statement">
            <div>
              <div className="eyebrow !text-white/60">{city.name} promise</div>
              <div className="mt-2 font-display font-black text-xl md:text-2xl tracking-tight">
                We serve {city.name} homeowners 24/7 — typical response time under 60 minutes.
              </div>
            </div>
            <CallButton testId="city-trust-call-button" />
          </div>

          {/* Services grid */}
          <div className="mt-14">
            <div className="eyebrow">Available in {city.name}</div>
            <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#1B2A4A]">
              All 8 services available across {city.name}.
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {SERVICES.map((s) => (
                <ServiceCard key={s.slug} service={s} />
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="mt-14 relative rounded-sm bg-[#F8FAFC] border border-[#E2E8F0] p-6 md:p-10" data-testid="city-testimonial">
            <Quotes size={52} weight="fill" className="text-[#FF6B00] absolute -top-4 left-6" />
            <div className="flex items-center gap-1 text-[#FF6B00] mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={20} weight="fill" />
              ))}
            </div>
            <p className="mt-4 text-lg md:text-xl text-[#1B2A4A] leading-relaxed italic">
              &ldquo;{city.testimonial.quote}&rdquo;
            </p>
            <div className="mt-6 font-display font-bold text-[#1B2A4A]">
              {city.testimonial.name}
              <span className="ml-2 text-sm text-[#4B5563] font-medium">
                — {city.testimonial.city}
              </span>
            </div>
          </div>

          {/* Map */}
          <div className="mt-14">
            <div className="eyebrow">Coverage zone</div>
            <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#1B2A4A]">
              All of {city.name}, on-call 24/7.
            </h2>
            <div className="mt-6">
              <StaticMap city={city.name} testId={`city-map-${city.slug}`} />
            </div>
          </div>

          {/* Nearby cities */}
          <div className="mt-14">
            <div className="eyebrow">Also serving nearby</div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {otherCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/areas/${c.slug}`}
                  data-testid={`nearby-city-${c.slug}`}
                  className="rounded-sm border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-bold font-display text-[#1B2A4A] hover:border-[#FF6B00] hover:text-[#FF6B00] text-center transition-all"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTABanner
        headline={`${city.name} — Call Now. Tech dispatched.`}
        subtext={`Licensed technicians covering ${city.name} around the clock.`}
        testId={`city-${city.slug}-final-cta`}
      />
    </>
  );
}
